import type {
  DashboardShell,
  WidgetDataDto,
  ScalarDataDto,
  SeriesDataDto,
  SeriesMultiDataDto,
  TimeseriesDataDto,
  RowsDataDto,
} from '../types';

export const mockDashboardShell: DashboardShell = {
  id: 'dash-tenant-001',
  widgets: [
    {
      id: 'w-001',
      type: 'counter',
      title: 'Tareas totales',
      layout: {
        desktop: { x: 0, y: 0, w: 3, h: 2 },
        tablet: { x: 0, y: 0, w: 3, h: 2 },
        mobile: { x: 0, y: 0, w: 12, h: 2 },
      },
      config: { query: { dateRangeField: 'createdAt', dateRangePreset: 'last_30' } },
    },
    {
      id: 'w-002',
      type: 'counter',
      title: 'Tareas completadas',
      layout: {
        desktop: { x: 3, y: 0, w: 3, h: 2 },
        tablet: { x: 3, y: 0, w: 3, h: 2 },
        mobile: { x: 0, y: 2, w: 12, h: 2 },
      },
      config: { query: { dateRangeField: 'dueDate', dateRangePreset: 'last_30' } },
    },
    {
      id: 'w-003',
      type: 'counter',
      title: 'Tareas vencidas',
      layout: {
        desktop: { x: 6, y: 0, w: 3, h: 2 },
        tablet: { x: 0, y: 2, w: 3, h: 2 },
        mobile: { x: 0, y: 4, w: 12, h: 2 },
      },
      config: {},
    },
    {
      id: 'w-004',
      type: 'counter',
      title: 'Tasa de completado',
      layout: {
        desktop: { x: 9, y: 0, w: 3, h: 2 },
        tablet: { x: 3, y: 2, w: 3, h: 2 },
        mobile: { x: 0, y: 6, w: 12, h: 2 },
      },
      config: {},
    },
    {
      id: 'w-005',
      type: 'bar',
      title: 'Tareas por miembro',
      layout: {
        desktop: { x: 0, y: 2, w: 6, h: 3 },
        tablet: { x: 0, y: 4, w: 6, h: 3 },
        mobile: { x: 0, y: 8, w: 12, h: 3 },
      },
      config: {
        query: { dateRangeField: 'createdAt', dateRangePreset: 'last_30' },
      },
    },
    {
      id: 'w-006',
      type: 'donut',
      title: 'Tareas por estado',
      layout: {
        desktop: { x: 6, y: 2, w: 6, h: 3 },
        tablet: { x: 0, y: 7, w: 6, h: 3 },
        mobile: { x: 0, y: 11, w: 12, h: 3 },
      },
      config: {},
    },
    {
      id: 'w-007',
      type: 'line',
      title: 'Tareas completadas por día',
      layout: {
        desktop: { x: 0, y: 5, w: 12, h: 4 },
        tablet: { x: 0, y: 10, w: 6, h: 4 },
        mobile: { x: 0, y: 14, w: 12, h: 4 },
      },
      config: {
        query: { dateRangeField: 'dueDate', dateRangePreset: 'last_30' },
      },
    },
    {
      id: 'w-008',
      type: 'table',
      title: 'Rendimiento por miembro',
      layout: {
        desktop: { x: 0, y: 9, w: 12, h: 6 },
        tablet: { x: 0, y: 14, w: 6, h: 6 },
        mobile: { x: 0, y: 18, w: 12, h: 6 },
      },
      config: {},
    },
  ],
};

const mockScalar = (widgetId: string, value: number): ScalarDataDto => ({
  widgetId,
  shape: 'scalar',
  value,
});

const mockSeries = (widgetId: string): SeriesDataDto => ({
  widgetId,
  shape: 'series',
  labels: ['Alice Martínez', 'Bob García', 'Carlos López', 'Diana Torres', 'Eduardo Ruiz'],
  values: [18, 12, 9, 7, 5],
});

const mockDonut = (widgetId: string): SeriesDataDto => ({
  widgetId,
  shape: 'series',
  labels: ['Completado', 'En progreso', 'Pendiente', 'Vencido'],
  values: [42, 18, 11, 5],
});

const mockSeriesMulti = (widgetId: string): SeriesMultiDataDto => ({
  widgetId,
  shape: 'series_multi',
  labels: ['Proyecto Alpha', 'Proyecto Beta', 'Proyecto Gamma'],
  series: [
    { label: 'Completado', values: [8, 5, 12] },
    { label: 'En progreso', values: [3, 7, 4] },
    { label: 'Pendiente', values: [2, 1, 6] },
  ],
});

const mockTimeseries = (widgetId: string): TimeseriesDataDto => ({
  widgetId,
  shape: 'timeseries',
  labels: ['Jun 1', 'Jun 5', 'Jun 10', 'Jun 15', 'Jun 20', 'Jun 25', 'Jun 30'],
  values: [3, 7, 5, 12, 8, 15, 10],
});

const mockRows = (widgetId: string): RowsDataDto => ({
  widgetId,
  shape: 'rows',
  columns: ['member', 'assigned', 'completed', 'completionRate', 'overdue'],
  rows: [
    ['Alice Martínez', 20, 17, 85.0, 1],
    ['Bob García', 15, 10, 66.7, 3],
    ['Carlos López', 12, 11, 91.7, 0],
    ['Diana Torres', 8, 5, 62.5, 2],
  ],
});

export const mockWidgetDataMap: Record<string, WidgetDataDto> = {
  'w-001': mockScalar('w-001', 76),
  'w-002': mockScalar('w-002', 42),
  'w-003': mockScalar('w-003', 6),
  'w-004': mockScalar('w-004', 72),
  'w-005': mockSeries('w-005'),
  'w-006': mockDonut('w-006'),
  'w-007': mockTimeseries('w-007'),
  'w-008': mockRows('w-008'),
};

export const mockWidgetData = {
  scalar: mockScalar,
  series: mockSeries,
  seriesMulti: mockSeriesMulti,
  timeseries: mockTimeseries,
  rows: mockRows,
};
